import { useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_CHUNK_SIZE = 48;

// Renders a long list a chunk at a time. The caller places `sentinelRef` on an
// element after the rendered items; whenever it scrolls into view the next
// chunk is revealed. Large grids of thumbnails otherwise fire hundreds of image
// requests in one go and trip the per-IP rate limit in front of the API.
export const useIncrementalList = <T>(
  items: T[],
  chunkSize: number = DEFAULT_CHUNK_SIZE,
) => {
  const [visibleCount, setVisibleCount] = useState(chunkSize);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // A new list (filter, sort, search) starts again from the first chunk.
  useEffect(() => {
    setVisibleCount(chunkSize);
  }, [items, chunkSize]);

  const hasMore = visibleCount < items.length;

  const showMore = useCallback(() => {
    setVisibleCount((count) => Math.min(count + chunkSize, items.length));
  }, [chunkSize, items.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!hasMore || !sentinel || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) showMore();
      },
      // Start loading the next chunk a bit before the user reaches the end.
      { rootMargin: '600px 0px' },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, showMore]);

  return {
    visibleItems: hasMore ? items.slice(0, visibleCount) : items,
    hasMore,
    showMore,
    sentinelRef,
  };
};
